import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { Loader, EmptyState } from "../components/KpiCard";
import { FileText, FileImage, Download, Trash2, UploadCloud, Loader2 } from "lucide-react";

const CATEGORIES = ["General", "Invoice", "Receipt", "Grant Agreement", "Contract", "Bank Statement", "Tax / IRS", "Board Document"];

function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

export default function Documents() {
  const { user } = useAuth();
  const canDelete = ["admin", "finance_officer"].includes(user?.role);
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("General");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState("All");
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("description", description);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Document removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!docs) return <Loader />;
  const shown = filter === "All" ? docs : docs.filter((d) => d.category === filter);

  return (
    <div>
      <PageHeader title="Document Management" subtitle="Securely store invoices, grant agreements, receipts and board documents." />

      <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6" data-testid="upload-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category"
              className="w-full bg-white border border-stone-300 rounded-md px-3 py-2 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="doc-description"
              className="w-full bg-white border border-stone-300 rounded-md px-3 py-2 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15" />
          </div>
          <div>
            <input ref={fileRef} type="file" onChange={onUpload} className="hidden" data-testid="doc-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn"
              className="w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload Document"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-200 ${filter === c ? "bg-forest text-white border-forest" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50"}`}>
            {c}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState title="No documents" hint="Upload your first document above." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {shown.map((d) => {
            const isImg = (d.content_type || "").startsWith("image/");
            return (
              <div key={d.id} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 transition-colors duration-200" data-testid={`doc-${d.id}`}>
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center text-forest">
                    {isImg ? <FileImage className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                  </div>
                  <StatusBadge tone="blue">{d.category}</StatusBadge>
                </div>
                <div className="mt-3 font-medium text-sm text-[#1A1A1A] truncate" title={d.original_filename}>{d.original_filename}</div>
                {d.description && <div className="text-xs text-[#5C5C54] mt-0.5 truncate">{d.description}</div>}
                <div className="text-xs text-[#8A9F95] mt-1">{bytes(d.size)} · {fmtDate(d.created_at)}</div>
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-stone-100">
                  <button onClick={() => download(d)} data-testid={`download-${d.id}`} className="text-xs text-forest font-medium hover:underline flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Download</button>
                  {canDelete && <button onClick={() => remove(d.id)} data-testid={`delete-${d.id}`} className="text-xs text-stone-400 hover:text-outflow flex items-center gap-1 ml-auto"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
